import React from "react";
import { Box, Button, IconButton, Typography, alpha } from "@mui/material";
import {
    Add,
    Close as CloseIcon,
    Remove,
    Restaurant,
    ShoppingCart,
} from "@mui/icons-material";
import ImageLoader from "components/ImageLoader";
import { useTheme } from "@mui/material/styles";
import { formatCurrency } from "../../../utils/currencyUtils";
import { LUXURY } from "data/guestLuxuryTheme";

const DesktopCartPanel = ({
    luxury = false,
    cartItems,
    onClearCart,
    onRemoveItem,
    onUpdateQuantity,
    onOpenCheckout,
    cartTotal,
    cartAccent,
    cartBtnGradient,
    cartBtnGradientHover,
    cartBtnShadow,
    cartBtnShadowHover,
}) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                width: "400px",
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: luxury ? LUXURY.page : "#f1f5f9",
                    boxShadow: `-8px 0 32px ${alpha(theme.palette.common.black, 0.12)}`,
                }}
            >
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        background: luxury ? LUXURY.barGradient : cartBtnGradient,
                        flexShrink: 0,
                        boxShadow: cartBtnShadow,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 2.5,
                                    bgcolor: alpha("#ffffff", 0.18),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: `1px solid ${alpha("#ffffff", 0.22)}`,
                                }}
                            >
                                <ShoppingCart sx={{ fontSize: 22, color: "#ffffff" }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#ffffff", lineHeight: 1.1, letterSpacing: 0.2 }}>
                                    Your Order
                                </Typography>
                                <Typography sx={{ fontSize: "0.72rem", color: alpha("#ffffff", 0.75), fontWeight: 500 }}>
                                    {cartItems.length} {cartItems.length !== 1 ? "items" : "item"} in cart
                                </Typography>
                            </Box>
                        </Box>
                        {cartItems.length > 0 && (
                            <IconButton
                                size="small"
                                onClick={onClearCart}
                                sx={{
                                    color: alpha("#ffffff", 0.7),
                                    "&:hover": { bgcolor: alpha("#ffffff", 0.12), color: "#ffffff" },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        p: 1.75,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                        "&::-webkit-scrollbar": { width: 5 },
                        "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                        "&::-webkit-scrollbar-thumb": { bgcolor: alpha(theme.palette.primary.main, 0.25), borderRadius: 10 },
                    }}
                >
                    {cartItems.length === 0 ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 6,
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: "50%",
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                                }}
                            >
                                <ShoppingCart sx={{ fontSize: 36, color: alpha(theme.palette.primary.main, 0.4) }} />
                            </Box>
                            <Typography sx={{ fontWeight: 700, color: "#475569", fontSize: "0.95rem" }}>Your cart is empty</Typography>
                            <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8", textAlign: "center" }}>Browse the menu and add items to get started</Typography>
                        </Box>
                    ) : (
                        cartItems.map((item, index) => {
                            const basePrice = item.variation?.price
                                ? parseFloat(item.variation.price) || 0
                                : parseFloat(item.menuItem?.basePrice || item.menuItem?.price || 0) || 0;
                            const modifiersPrice = (item.modifiers || []).reduce(
                                (sum, mod) => sum + (parseFloat(mod.price) || 0),
                                0,
                            );
                            const unitPrice = basePrice + modifiersPrice;
                            const itemTotal = unitPrice * item.quantity;

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        bgcolor: luxury ? LUXURY.card : "#ffffff",
                                        borderRadius: 3.5,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.07)}`,
                                        overflow: "visible",
                                        transition: "all 0.22s ease",
                                        "&:hover": {
                                            boxShadow: `0 8px 28px ${alpha(theme.palette.common.black, 0.13)}`,
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >
                                    <Box sx={{ p: 1.5, display: "flex", gap: 1.5 }}>
                                        <Box sx={{ position: "relative", flexShrink: 0 }}>
                                            <Box
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    borderRadius: 3,
                                                    overflow: "hidden",
                                                    bgcolor: "#f1f5f9",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                                                }}
                                            >
                                                {item.menuItem?.image ? (
                                                    <ImageLoader
                                                        imagePath={item.menuItem.image}
                                                        alt={item.menuItem.name}
                                                        width={80}
                                                        height={80}
                                                        rounded={false}
                                                        borderRadius={0}
                                                    />
                                                ) : (
                                                    <Restaurant sx={{ fontSize: 32, color: alpha(theme.palette.text.secondary, 0.25) }} />
                                                )}
                                            </Box>
                                        </Box>

                                        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.5 }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#0f172a", lineHeight: 1.35, flex: 1, minWidth: 0, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                                    {item.menuItem?.name}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onRemoveItem(index)}
                                                    sx={{
                                                        p: 0.35,
                                                        mt: -0.25,
                                                        ml: 0.5,
                                                        flexShrink: 0,
                                                        color: "#cbd5e1",
                                                        "&:hover": { color: theme.palette.error.main, bgcolor: alpha(theme.palette.error.main, 0.08) },
                                                        transition: "all 0.18s",
                                                    }}
                                                >
                                                    <CloseIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Box>

                                            {(item.variation || (item.modifiers && item.modifiers.length > 0)) && (
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {item.variation && (
                                                        <Box
                                                            sx={{
                                                                height: 18,
                                                                px: 0.75,
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                borderRadius: 1,
                                                                fontSize: "0.62rem",
                                                                fontWeight: 700,
                                                                bgcolor: luxury ? alpha(LUXURY.orange, 0.1) : "rgba(107,125,207,0.09)",
                                                                color: cartAccent,
                                                            }}
                                                        >
                                                            {item.variation.name}
                                                        </Box>
                                                    )}
                                                    {(item.modifiers || []).map((mod, i) => (
                                                        <Box
                                                            key={i}
                                                            sx={{
                                                                height: 18,
                                                                px: 0.7,
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                borderRadius: 1,
                                                                fontSize: "0.6rem",
                                                                fontWeight: 600,
                                                                bgcolor: "#f1f5f9",
                                                                color: "#64748b",
                                                            }}
                                                        >
                                                            {mod.name}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "auto", pt: 0.5 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        bgcolor: "#f8fafc",
                                                        borderRadius: 10,
                                                        border: `1.5px solid ${alpha(theme.palette.divider, 0.22)}`,
                                                        p: 0.4,
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                                                        disabled={item.quantity <= 1}
                                                        sx={{
                                                            width: 26,
                                                            height: 26,
                                                            p: 0,
                                                            bgcolor: item.quantity > 1 ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                                                            color: item.quantity > 1 ? cartAccent : "#cbd5e1",
                                                            borderRadius: "50%",
                                                            "&:hover": { bgcolor: "rgba(107,125,207,0.24)" },
                                                        }}
                                                    >
                                                        <Remove sx={{ fontSize: 13 }} />
                                                    </IconButton>
                                                    <Typography sx={{ px: 0.75, fontWeight: 700, fontSize: "0.9rem", minWidth: 22, textAlign: "center", color: "#0f172a", letterSpacing: -0.5 }}>
                                                        {item.quantity}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                                                        sx={{
                                                            width: 26,
                                                            height: 26,
                                                            p: 0,
                                                            background: cartBtnGradient,
                                                            color: "#ffffff",
                                                            borderRadius: "50%",
                                                            boxShadow: cartBtnShadow,
                                                            "&:hover": {
                                                                background: cartBtnGradientHover,
                                                                boxShadow: cartBtnShadowHover,
                                                            },
                                                        }}
                                                    >
                                                        <Add sx={{ fontSize: 13 }} />
                                                    </IconButton>
                                                </Box>
                                                <Box sx={{ textAlign: "right" }}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: cartAccent, lineHeight: 1, letterSpacing: -0.3 }}>
                                                        {formatCurrency(itemTotal)}
                                                    </Typography>
                                                    {item.quantity > 1 && (
                                                        <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", mt: 0.25 }}>
                                                            {formatCurrency(unitPrice)} × {item.quantity}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                </Box>

                {cartItems.length > 0 && (
                    <Box
                        sx={{
                            px: 2.25,
                            pt: 2,
                            pb: 2.5,
                                                            bgcolor: luxury ? LUXURY.page : "#ffffff",
                            flexShrink: 0,
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                            boxShadow: `0 -4px 16px ${alpha(theme.palette.common.black, 0.05)}`,
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.75 }}>
                            <Typography sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.88rem" }}>Grand Total</Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: "1.4rem", color: cartAccent, lineHeight: 1 }}>
                                {formatCurrency(cartTotal || 0)}
                            </Typography>
                        </Box>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={onOpenCheckout}
                            sx={{
                                borderRadius: 3,
                                py: 1.6,
                                background: cartBtnGradient,
                                fontWeight: 800,
                                fontSize: "0.98rem",
                                textTransform: "none",
                                letterSpacing: 0.3,
                                boxShadow: cartBtnShadow,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    background: cartBtnGradientHover,
                                    transform: "translateY(-1px)",
                                    boxShadow: cartBtnShadowHover,
                                },
                            }}
                        >
                            Proceed to Checkout
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default DesktopCartPanel;
